import { Component, computed, inject, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EEventType } from '@enums/event-tracking.enum';
import { TSocialContent } from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode } from '@interfaces/campaign-analytics.interface';
import { SocialContentTilesListComponent } from '@pages/campaigns/campaign-details/social-content-tiles-list/social-content-tiles-list.component';
import { ICreator } from '@pages/creators/creators.interface';
import { ITileConfiguration } from '@shared/components/social-content-tile/social-content-tile.interface';
import { CreatorDetailsSocialTilesService } from './creator-details-social-tiles.service';
import { CreatorSocialContentDialogComponent } from './creator-social-content-dialog/creator-social-content-dialog.component';
import { ICreatorSocialContentDialogData } from './creator-social-content-dialog/creator-social-content-dialog.interface';

@Component({
  imports: [SocialContentTilesListComponent],
  selector: 'app-creator-details-social-tiles',
  templateUrl: './creator-details-social-tiles.component.html',
})
export class CreatorDetailsSocialTilesComponent {
  public readonly socialContents = input.required<TSocialContent[] | null>();
  public readonly creator = input.required<ICreator | null>();

  public readonly loading = input<boolean>(false);

  public readonly analyticsNodes = computed((): IAnalyticsNode[] =>
    this.creatorDetailsSocialTilesService.createAnalyticsNodes(this.socialContents(), this.creator()),
  );

  private readonly creatorDetailsSocialTilesService = inject(CreatorDetailsSocialTilesService);
  private readonly dialog = inject(MatDialog);
  private readonly eventTrackingService = inject(EventTrackingService);

  public onTileClick(tileConfiguration: ITileConfiguration): void {
    const data: ICreatorSocialContentDialogData = {
      tileConfiguration,
    };

    const dialogRef = this.dialog.open(CreatorSocialContentDialogComponent, {
      data,
      maxWidth: '1800px',
      width: '90%',
    });

    // Same pattern as cta-analytics-dialog.component.ts - a local timestamp at open is enough,
    // since only one content dialog can be open at a time (this component instance exists only
    // while it's open).
    const openedAt = Date.now();

    this.eventTrackingService.track(EEventType.creatorContentOpened, {
      creatorId: this.creator()?.id,
      socialContentId: tileConfiguration.id,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.eventTrackingService.track(EEventType.creatorContentClosed, {
        creatorId: this.creator()?.id,
        socialContentId: tileConfiguration.id,
        timeSpent: Date.now() - openedAt,
      });
    });
  }
}
